import { clsx } from 'clsx'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { PDF_TO_CSS, rotatedSize } from '@/lib/geometry'
import { markupSelection } from '@/lib/selection'
import { useDoc } from '@/store/documentStore'
import { useSearch } from '@/store/searchStore'
import { MAX_ZOOM, MIN_ZOOM, useUi } from '@/store/uiStore'
import { PageView } from './PageView'
import { SelectionMenu } from './SelectionMenu'

const GAP = 16
const PAD_Y = 20

interface Layout {
  tops: number[]
  lefts: number[]
  height: number
  width: number
  scale: number
}

function upperBound(arr: number[], v: number) {
  let lo = 0
  let hi = arr.length
  while (lo < hi) {
    const mid = (lo + hi) >> 1
    if (arr[mid] <= v) lo = mid + 1
    else hi = mid
  }
  return lo
}

/**
 * Continuous-scroll, virtualized page viewer. Only pages near the viewport get canvases,
 * text layers and annotation overlays, so 1,000-page documents scroll smoothly.
 */
export function Viewer() {
  const pages = useDoc((s) => s.pages)
  const zoom = useUi((s) => s.zoom)
  const fit = useUi((s) => s.fit)
  const tool = useUi((s) => s.tool)
  const night = useUi((s) => s.nightMode)
  const scrollRequest = useUi((s) => s.scrollRequest)
  const byPage = useSearch((s) => s.byPage)
  const activeHit = useSearch((s) => (s.active >= 0 ? s.matches[s.active] : null))

  const scroller = useRef<HTMLDivElement>(null)
  const [viewport, setViewport] = useState({ top: 0, height: 800, width: 1000 })
  const scale = zoom * PDF_TO_CSS
  const sidePad = viewport.width < 640 ? 8 : 24

  const layout: Layout = useMemo(() => {
    const tops: number[] = []
    const lefts: number[] = []
    let y = PAD_Y
    let maxW = 0
    for (const p of pages) {
      const { w } = rotatedSize(p)
      maxW = Math.max(maxW, w * scale)
    }
    const contentW = Math.max(viewport.width, maxW + sidePad * 2)
    for (const p of pages) {
      const { w, h } = rotatedSize(p)
      tops.push(y)
      lefts.push((contentW - w * scale) / 2)
      y += h * scale + GAP
    }
    return { tops, lefts, height: y - GAP + PAD_Y + 80, width: contentW, scale }
  }, [pages, scale, viewport.width, sidePad])

  // ----- fit modes -----
  const currentPage = useUi((s) => s.currentPage)
  useLayoutEffect(() => {
    if (!fit || !pages.length) return
    const maxW = Math.max(...pages.map((p) => rotatedSize(p).w))
    const byWidth = (viewport.width - sidePad * 2) / (maxW * PDF_TO_CSS)
    let z = byWidth
    if (fit === 'page') {
      const ref = pages[Math.min(currentPage, pages.length - 1)]
      const { h } = rotatedSize(ref)
      z = Math.min(byWidth, (viewport.height - PAD_Y * 2) / (h * PDF_TO_CSS))
    }
    z = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, z))
    if (Math.abs(z - useUi.getState().zoom) > 0.001) useUi.getState().setZoom(z, fit)
  }, [fit, pages, viewport.width, viewport.height, sidePad, currentPage])

  // ----- viewport tracking -----
  useLayoutEffect(() => {
    const el = scroller.current
    if (!el) return
    const update = () => setViewport({ top: el.scrollTop, height: el.clientHeight, width: el.clientWidth })
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    let raf = 0
    const onScroll = () => {
      cancelAnimationFrame(raf)
      raf = requestAnimationFrame(update)
    }
    el.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      ro.disconnect()
      el.removeEventListener('scroll', onScroll)
      cancelAnimationFrame(raf)
    }
  }, [])

  // current page = the page under the upper third of the viewport
  useEffect(() => {
    if (!layout.tops.length) return
    const probe = viewport.top + viewport.height * 0.33
    const idx = Math.max(0, upperBound(layout.tops, probe) - 1)
    if (idx !== useUi.getState().currentPage) useUi.getState().setCurrentPage(idx)
  }, [viewport, layout])

  // ----- zoom anchoring: keep the point under the cursor (or the center) fixed -----
  const anchor = useRef<{ x: number; y: number } | null>(null)
  const prevLayout = useRef<Layout>(layout)
  useLayoutEffect(() => {
    const el = scroller.current
    const old = prevLayout.current
    prevLayout.current = layout
    if (!el || old.scale === layout.scale || !old.tops.length) return
    const atTop = !anchor.current && el.scrollTop <= 1
    const a = anchor.current ?? { x: el.clientWidth / 2, y: el.clientHeight / 2 }
    anchor.current = null
    const yContent = el.scrollTop + a.y
    const i = Math.max(0, upperBound(old.tops, yContent) - 1)
    const offPt = (yContent - old.tops[i]) / old.scale
    const newTop = (layout.tops[i] ?? 0) + offPt * layout.scale - a.y
    const xContent = el.scrollLeft + a.x
    const newLeft = old.width ? (xContent * layout.width) / old.width - a.x : 0
    el.scrollTop = atTop ? 0 : Math.max(0, newTop)
    el.scrollLeft = Math.max(0, newLeft)
    setViewport({ top: el.scrollTop, height: el.clientHeight, width: el.clientWidth })
  }, [layout])

  const zoomAt = useCallback((z: number, clientX?: number, clientY?: number) => {
    const el = scroller.current
    if (el && clientX !== undefined && clientY !== undefined) {
      const r = el.getBoundingClientRect()
      anchor.current = { x: clientX - r.left, y: clientY - r.top }
    }
    useUi.getState().setZoom(z, null)
  }, [])

  // ----- scroll requests (page nav, outline, search) -----
  useEffect(() => {
    const el = scroller.current
    if (!el || !scrollRequest) return
    const { index, top } = scrollRequest
    const t = layout.tops[index]
    if (t === undefined) return
    const page = pages[index]
    const extra = top !== undefined && page && page.rotation === 0 ? top * layout.scale : 0
    el.scrollTo({ top: Math.max(0, t + extra - (top === undefined ? PAD_Y / 2 : 0)), behavior: 'auto' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollRequest])

  // ----- ctrl/⌘ + wheel and trackpad pinch zoom -----
  useEffect(() => {
    const el = scroller.current
    if (!el) return
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return
      e.preventDefault()
      const factor = Math.exp(-e.deltaY * (e.deltaMode === 1 ? 0.05 : 0.0025))
      zoomAt(useUi.getState().zoom * factor, e.clientX, e.clientY)
    }
    // Safari trackpad pinch
    let gestureStart = 1
    const onGestureStart = (e: Event) => {
      e.preventDefault()
      gestureStart = useUi.getState().zoom
    }
    const onGestureChange = (e: Event) => {
      e.preventDefault()
      const g = e as Event & { scale: number; clientX: number; clientY: number }
      zoomAt(gestureStart * g.scale, g.clientX, g.clientY)
    }
    el.addEventListener('wheel', onWheel, { passive: false })
    el.addEventListener('gesturestart', onGestureStart)
    el.addEventListener('gesturechange', onGestureChange)
    return () => {
      el.removeEventListener('wheel', onWheel)
      el.removeEventListener('gesturestart', onGestureStart)
      el.removeEventListener('gesturechange', onGestureChange)
    }
  }, [zoomAt])

  // ----- touch pinch-zoom, hand-tool panning, markup-on-select -----
  const touches = useRef(new Map<number, { x: number; y: number }>())
  const pinch = useRef<{
    dist: number
    zoom: number
    mid: { x: number; y: number }
    manualPan: boolean
  } | null>(null)

  const onPointerDown = (e: React.PointerEvent) => {
    const el = scroller.current
    if (!el) return
    if (e.pointerType === 'touch') {
      touches.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
      if (touches.current.size === 2) {
        const [a, b] = [...touches.current.values()]
        const target = e.target as HTMLElement
        pinch.current = {
          dist: Math.hypot(a.x - b.x, a.y - b.y),
          zoom: useUi.getState().zoom,
          mid: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
          manualPan: target.closest<HTMLElement>('.ann-layer')?.style.touchAction === 'none',
        }
      }
    }
    if (tool === 'hand' && e.pointerType === 'mouse' && e.button === 0) {
      const sx = e.clientX
      const sy = e.clientY
      const st = el.scrollTop
      const sl = el.scrollLeft
      el.style.cursor = 'grabbing'
      const move = (ev: PointerEvent) => {
        el.scrollTop = st - (ev.clientY - sy)
        el.scrollLeft = sl - (ev.clientX - sx)
      }
      const up = () => {
        el.style.cursor = ''
        window.removeEventListener('pointermove', move)
        window.removeEventListener('pointerup', up)
      }
      window.addEventListener('pointermove', move)
      window.addEventListener('pointerup', up)
    }
  }

  const onPointerMove = (e: React.PointerEvent) => {
    if (e.pointerType !== 'touch' || !touches.current.has(e.pointerId)) return
    touches.current.set(e.pointerId, { x: e.clientX, y: e.clientY })
    const p = pinch.current
    if (!p || touches.current.size < 2) return
    const [a, b] = [...touches.current.values()]
    const dist = Math.hypot(a.x - b.x, a.y - b.y)
    const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }
    if (p.manualPan && scroller.current) {
      scroller.current.scrollLeft -= mid.x - p.mid.x
      scroller.current.scrollTop -= mid.y - p.mid.y
    }
    p.mid = mid
    if (p.dist > 0) {
      const z = p.zoom * (dist / p.dist)
      if (Math.abs(z - useUi.getState().zoom) / z > 0.01) zoomAt(z, mid.x, mid.y)
    }
  }

  const onPointerUp = (e: React.PointerEvent) => {
    touches.current.delete(e.pointerId)
    if (touches.current.size < 2) pinch.current = null
    if ((tool === 'highlight' || tool === 'underline' || tool === 'strikeout') && e.pointerType === 'mouse') {
      setTimeout(() => markupSelection(tool), 0)
    }
  }

  // ----- render window -----
  const renderFrom = viewport.top - viewport.height * 1.2
  const renderTo = viewport.top + viewport.height * 2.2
  const interFrom = viewport.top - viewport.height * 0.6
  const interTo = viewport.top + viewport.height * 1.6
  const start = Math.max(0, upperBound(layout.tops, renderFrom) - 2)
  const end = Math.min(pages.length, upperBound(layout.tops, renderTo) + 1)
  const visible = []
  for (let i = start; i < end; i++) {
    const p = pages[i]
    const top = layout.tops[i]
    const h = rotatedSize(p).h * scale
    if (top + h < renderFrom || top > renderTo) continue
    visible.push(
      <PageView
        key={p.id}
        page={p}
        index={i}
        scale={scale}
        top={top}
        left={layout.lefts[i]}
        render
        interactive={top + h >= interFrom && top <= interTo}
        hits={byPage.get(p.id)}
        activeHit={activeHit}
      />,
    )
  }

  const selectable = tool === 'select' || tool === 'highlight' || tool === 'underline' || tool === 'strikeout'

  return (
    <div
      ref={scroller}
      data-testid="viewer"
      tabIndex={-1}
      className={clsx(
        'relative h-full w-full overflow-auto overscroll-contain bg-canvas outline-none',
        selectable && 'text-selectable',
        `tool-${tool}`,
        (tool === 'select' || tool === 'hand') && 'allow-links',
        tool === 'hand' && 'cursor-grab',
        night && 'night',
      )}
      style={{ touchAction: 'pan-x pan-y' }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      <div className="relative" style={{ height: layout.height, width: layout.width }}>
        {visible}
      </div>
      <SelectionMenu />
    </div>
  )
}
